import { useMap } from "@vis.gl/react-google-maps";
import { useEffect, useRef, useMemo } from "react";
import { useTrips } from "@/api/trips";
import { useShortcutsStore } from "@/screens/dashboard/shortcuts.store";
import { useCarrierScope } from "@/contexts/carrier-scope-context";

export function AllTripsDirections() {
  const { tripFilter } = useShortcutsStore();
  const map = useMap();
  const previousPolylines = useRef<google.maps.Polyline[]>([]);

  const { carrierIds, dispatchCenterIds } = useCarrierScope();

  const { data: tripsResponse } = useTrips({
    status: "PLANNED,ACTIVE,COMPLETED",
    carrierIds: carrierIds.join(","),
    dispatchCenterIds: dispatchCenterIds.join(","),
  });

  const trips = useMemo(() => tripsResponse?.data ?? [], [tripsResponse]);

  // Filter trips based on the tripFilter
  const filteredTrips = useMemo(() => {
    if (tripFilter.showAll) {
      // If showing all, potentially hide specific trips
      return tripFilter.hiddenTripIds.length > 0
        ? trips.filter((trip) => !tripFilter.hiddenTripIds.includes(trip.id))
        : trips;
    } else if (tripFilter.visibleTripIds.length > 0) {
      // Only show specific trips
      return trips.filter((trip) =>
        tripFilter.visibleTripIds.includes(trip.id)
      );
    }
    // If not showing all and no specific trips to show
    return [];
  }, [trips, tripFilter]);

  useEffect(() => {
    if (!map || !tripsResponse?.data.length) {
      return;
    }

    // Clear existing polylines
    previousPolylines.current.forEach((polyline) => polyline.setMap(null));
    previousPolylines.current = [];

    // If no trips to show after filtering, return early
    if (filteredTrips.length === 0) {
      return;
    }

    filteredTrips.forEach((trip) => {
      const routes = trip.plannedRoute?.routes ?? [];
      routes.forEach((route: { polyline?: { encodedPolyline?: string } }) => {
        const encodedPath = route.polyline?.encodedPolyline;
        if (!encodedPath) return;

        try {
          const path = google.maps.geometry.encoding.decodePath(encodedPath);
          if (!path || path.length === 0) {
            console.log("No path decoded for trip:", trip.id);
            return;
          }

          const arrow = {
            path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 3, // Size of the arrow
            strokeColor: "#000000", // Black outline for visibility
            strokeWeight: 1,
            fillColor: trip.color ?? "blue", // Fill with trip color
            fillOpacity: 1,
          };

          const polyline = new google.maps.Polyline({
            path,
            geodesic: true,
            strokeColor: trip.color ?? "blue",
            strokeOpacity: 0.7, // Slightly lower opacity to see arrows better
            strokeWeight: 4, // Slightly thinner line
            icons: [
              {
                icon: arrow,
                offset: "0%", // Start placing arrows from the beginning
                repeat: "100px", // Repeat arrow every 100 pixels
              },
            ],
          });

          polyline.setMap(map);
          previousPolylines.current.push(polyline);
        } catch (error) {
          console.error(
            `Failed to decode polyline for trip ${trip.id}:`,
            error
          );
        }
      });
    });

    return () => {
      previousPolylines.current.forEach((polyline) => polyline.setMap(null));
      // Restore original cleanup
      previousPolylines.current = [];
    };
  }, [map, tripsResponse, filteredTrips]);

  return null;
}
